import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function subscriptionRequest(method: string, body?: unknown) {
  const response = await fetch('/api/push-subscription', {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error('Bildirim kaydı yapılamadı. Lütfen tekrar deneyin.');
  return response.json();
}

export default function PaymentNotifications() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone;

  useEffect(() => {
    let active = true;
    if (supported() && Notification.permission === 'granted') {
      navigator.serviceWorker.register('/sw.js')
        .then(() => navigator.serviceWorker.ready)
        .then(reg => reg.pushManager.getSubscription())
        .then(async sub => {
          if (!sub || !active) return;
          // Rebind this device to the signed-in account; permission alone is not a saved subscription.
          await subscriptionRequest('POST', sub.toJSON());
          if (active) setEnabled(true);
        }).catch(() => { if (active) setMessage('Bildirim bağlantısı doğrulanamadı. Yeniden etkinleştirin.'); });
    }
    return () => { active = false; };
  }, [user?.id]);

  const enable = async () => {
    setBusy(true);
    setMessage('');
    try {
      // Must run directly from the tap, before network awaits (required on iPhone).
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setMessage('Bildirim izni verilmedi. Telefonunuzun veya tarayıcınızın site bildirim ayarlarından izin verebilirsiniz.');
        return;
      }
      const { publicKey } = await subscriptionRequest('GET');
      if (!publicKey) throw new Error('Bildirim servisi henüz hazır değil.');
      await navigator.serviceWorker.register('/sw.js');
      const reg = await navigator.serviceWorker.ready;
      const key = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      let sub = await reg.pushManager.getSubscription();
      if (sub?.options.applicationServerKey &&
          !new Uint8Array(sub.options.applicationServerKey).every((value, i) => value === key[i])) {
        await sub.unsubscribe();
        sub = null;
      }
      sub = sub || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
      await subscriptionRequest('POST', sub.toJSON());
      setEnabled(true);
      setMessage('Bu cihazda bildirimler etkin. Hatırlatılmasını istediğiniz ödemelerin zil simgesini açın.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Bildirimler etkinleştirilemedi.');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await subscriptionRequest('DELETE', { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setEnabled(false);
      setMessage('Bu cihazdaki bildirimler kapatıldı.');
    } catch { setMessage('Bildirimler kapatılamadı. Lütfen tekrar deneyin.'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    setMessage('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) throw new Error('Cihaz kaydı bulunamadı. Bildirimleri kapatıp yeniden etkinleştirin.');
      const response = await fetch('/api/push-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Deneme bildirimi gönderilemedi.');
      setMessage('Deneme bildirimi sağlayıcıya iletildi. Telefonunuzun bildirim merkezini kontrol edin; görünmüyorsa uygulamanın bildirim iznini ve Odak/Rahatsız Etmeyin ayarlarını kontrol edin.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Deneme bildirimi gönderilemedi.');
    } finally { setBusy(false); }
  };

  return (
    <div className="payment-notifications">
      <strong>Telefon bildirimleri</strong>
      <p>Hatırlatıcısı açık ödemeler, son ödeme gününden itibaren ödenene kadar her sabah Türkiye saatiyle 09.00’da hatırlatılır. Gecikmiş ödemeler de dahildir. Sayfanın açık olması gerekmez.</p>
      {ios && !standalone ? (
        <p>iPhone/iPad’de Safari’nin Paylaş menüsünden “Ana Ekrana Ekle”yi seçin. Uygulamayı ana ekrandan açıp bildirimleri etkinleştirin (iOS/iPadOS 16.4 veya üzeri).</p>
      ) : !supported() ? (
        <p>Bu tarayıcı telefon bildirimlerini desteklemiyor. Güncel bir tarayıcıdan açın.</p>
      ) : (
        <button type="button" className="confirm-button" disabled={busy} onClick={enabled ? disable : enable}>
          {busy ? 'İşleniyor…' : enabled ? 'Bu Cihazda Bildirimleri Kapat' : 'Bildirimleri Etkinleştir'}
        </button>
      )}
      {enabled && (
        <button type="button" className="confirm-button" disabled={busy} onClick={sendTest} style={{ marginLeft: 8 }}>
          Deneme Bildirimi Gönder
        </button>
      )}
      <p role="status" aria-live="polite">{message}</p>
    </div>
  );
}
